import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { kitchenAPI, ordersAPI, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import FeatureGate from '../components/FeatureGate';
import EmptyState from '../components/EmptyState';
import { useSSE } from '../hooks/useSSE';
import { useLanguage } from '../context/LanguageContext';
import { playKitchenSound } from '../utils/sound';

function timeSince(dateStr, justNowLabel, minShort, hourShort) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 1) return justNowLabel;
  if (diff < 60) return `${diff} ${minShort}`;
  return `${Math.floor(diff / 60)} ${hourShort} ${diff % 60} ${minShort}`;
}

// Tur 199: bekleme süresi tabanlı 4-state aciliyet sınıflaması
function getElapsedMin(orderTimeStr, nowTs) {
  if (!orderTimeStr) return 0;
  return Math.floor(((nowTs || Date.now()) - new Date(orderTimeStr)) / 60000);
}

function getUrgencyClass(min) {
  // border + arka plan
  if (min >= 30) return { card: 'border-red-500 bg-red-950/60', badge: 'bg-red-500/30 text-red-200 animate-pulse', late: true };
  if (min >= 20) return { card: 'border-amber-500 bg-amber-950/40', badge: 'bg-amber-500/30 text-amber-200', late: false };
  if (min >= 10) return { card: 'border-blue-500 bg-blue-950/30', badge: 'bg-blue-500/30 text-blue-200', late: false };
  return { card: 'border-green-600 bg-gray-800/80', badge: 'bg-green-500/20 text-green-300', late: false };
}

function OrderCard({ order, actionLabel, actionColor, onAction, prepEstimate, nowTs }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  // Tur 199: created_at öncelikli, fallback received_at / timestamp
  const orderTime = order.created_at || order.received_at || order.timestamp;
  const mins = getElapsedMin(orderTime, nowTs);
  const urgency = getUrgencyClass(mins);
  // IMP-10: tahmini kalan süre (prepEstimate varsa)
  const remaining = prepEstimate != null ? (prepEstimate - mins) : null;
  const remainCls =
    remaining == null ? '' :
    remaining < 0 ? 'bg-red-500/30 text-red-300' :
    remaining <= 5 ? 'bg-amber-500/30 text-amber-300' :
    'bg-emerald-500/30 text-emerald-300';

  const handleClick = async () => {
    setBusy(true);
    await onAction(order.id);
    setBusy(false);
  };

  const btnColors = {
    green: 'bg-green-500 active:bg-green-700',
    blue:  'bg-blue-500 active:bg-blue-700',
  };

  const statusLabel = order.status === 'preparing' ? (t('statusPreparing') || 'Hazırlanıyor')
    : order.status === 'ready' ? (t('statusReady') || 'Hazır')
    : (order.status || '');
  const lateHint = urgency.late ? ` — ${t('kitchenLateHint') || 'Gecikmiş!'}` : '';
  const cardLabel = `${t('orderLabel') || 'Sipariş'} #${order.id} — ${statusLabel}${lateHint}`;

  return (
    <div role="article" aria-label={cardLabel} className={`rounded-2xl border-2 p-4 flex flex-col gap-3 transition-colors ${urgency.card}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xl font-black text-white leading-tight">{order.table_name || `${t('tableNo')} #${order.table_id}`}</div>
          {order.waiter_name && (
            <div className="text-xs text-gray-400 mt-0.5">👤 {order.waiter_name}</div>
          )}
          {/* Tur 199: süre badge'i — her zaman görünür */}
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${urgency.badge}`}>
            {urgency.late && <span aria-hidden="true">⚠️</span>}
            🕐 {mins}{t('kitchenMinShort') || 'dk'}
          </span>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-xs font-bold px-2.5 py-1 rounded-xl ${
            mins >= 20 ? 'bg-red-500/30 text-red-300' :
            mins >= 10 ? 'bg-amber-500/30 text-amber-300' :
            'bg-gray-700/60 text-gray-300'
          }`}>
            ⏱ {timeSince(orderTime, t('justNow'), t('kitchenMinShort'), t('kitchenHourShort'))}
          </div>
          {remaining != null && (
            <div className={`text-[10px] font-semibold px-2 py-0.5 rounded-lg mt-1 ${remainCls}`}>
              {remaining < 0 ? `${Math.abs(remaining)} ${t('kitchenMinShort')} ${t('kitchenLate')}` : `${t('kitchenRemaining')} ≈${remaining} ${t('kitchenMinShort')}`}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-1">#{order.id}</div>
        </div>
      </div>

      {/* Items */}
      <div className="space-y-1.5">
        {(order.items || []).map(item => (
          <div key={item.id} className="flex items-center justify-between">
            <span className="text-gray-100 text-sm font-medium">{item.item_name || item.name}</span>
            <span className="text-white font-bold bg-gray-700/80 rounded-lg px-2.5 py-0.5 text-sm tabular-nums ml-2">×{item.quantity}</span>
          </div>
        ))}
      </div>

      {/* Note */}
      {order.notes && (
        <div className="bg-amber-500/15 border border-amber-500/40 rounded-xl px-3 py-2 text-amber-200 text-sm flex gap-2">
          <span>📝</span>
          <span>{order.notes}</span>
        </div>
      )}

      {/* Action button */}
      <button
        onClick={handleClick}
        disabled={busy}
        aria-label={`${actionLabel} — ${t('orderLabel') || 'Sipariş'} #${order.id}`}
        className={`w-full py-4 rounded-xl font-bold text-white text-base tracking-wide transition-all active:scale-[0.97] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 focus-visible:ring-white ${btnColors[actionColor]}`}
      >
        {busy ? `⏳ ${t('processing')}` : actionLabel}
      </button>
    </div>
  );
}

export default function Kitchen() {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ preparing: 0, ready: 0, served: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [prepEstimate, setPrepEstimate] = useState(null); // IMP-10
  // Tur 199: süre tick'i — her 30sn'de yenile (cleanup unmount'ta)
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);
  // Tur 141: kanban / unified list toggle
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('restaflex_kitchen_view') === 'kanban' ? 'kanban' : 'list'; } catch { return 'list'; }
  });
  const setView = (m) => {
    setViewMode(m);
    try { localStorage.setItem('restaflex_kitchen_view', m); } catch {}
  };
  const { user } = useAuth();
  const navigate = useNavigate();
  const cancelledRef = useRef(false);

  // IMP-10: tahmini hazırlık süresini dakikada bir güncelle (nadir değişir)
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      ordersAPI.getPrepTimeEstimate()
        .then(r => { if (!cancelled) setPrepEstimate(r.data.data?.avg_minutes ?? null); })
        .catch(() => { /* 403/500 — sessiz, badge gizli kalır */ });
    };
    load();
    const it = setInterval(load, 600000); // 10 dk
    return () => { cancelled = true; clearInterval(it); };
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        kitchenAPI.getOrders(),
        kitchenAPI.getStats(),
      ]);
      if (cancelledRef.current) return;
      setOrders(ordersRes.data.data || []);
      setStats(statsRes.data.data || { preparing: 0, ready: 0, served: 0 });
      setLastUpdate(new Date());
      setError('');
    } catch (err) {
      if (cancelledRef.current) return;
      setError(getErrorMessage(err, t('kitchenConnectionError')));
    } finally {
      if (cancelledRef.current) return;
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    cancelledRef.current = false;
    loadOrders();
    // AUDIT-16: SSE ana kanal (aşağıda useSSE), polling sadece safety-net olarak 5 dk
    const interval = setInterval(loadOrders, 300000);
    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
    };
  }, [loadOrders]);

  useSSE(({ type }) => {
    if (type === 'orders' || type === 'kitchen' || type === 'all') loadOrders();
  });

  // IMP-09: Yeni sipariş sesli bildirimi — 'preparing' sayısı arttığında preset ses.
  // Preset + volume kullanıcı tercihi (Settings → Bildirimler), localStorage'dan okunur.
  const prevPreparingCount = useRef(0);
  useEffect(() => {
    const current = orders.filter(o => o.status === 'preparing').length;
    if (prevPreparingCount.current > 0 && current > prevPreparingCount.current) {
      playKitchenSound();
    }
    prevPreparingCount.current = current;
  }, [orders]);

  const handleMarkServed = async (id) => {
    try { await kitchenAPI.markServed(id); if (cancelledRef.current) return; await loadOrders(); }
    catch (err) { if (cancelledRef.current) return; setError(getErrorMessage(err, t('actionFailed'))); }
  };
  // Tur 141: preparing → ready (kanban'da preparing kolonu butonu için)
  const handleMarkReady = async (id) => {
    try { await kitchenAPI.markReady(id); if (cancelledRef.current) return; await loadOrders(); }
    catch (err) { if (cancelledRef.current) return; setError(getErrorMessage(err, t('actionFailed'))); }
  };

  // Tur 310: kanban filter+sort — useMemo (was inline, recomputed every render)
  const preparing = useMemo(() => orders.filter(o => o.status === 'preparing'), [orders]);
  const ready = useMemo(() => orders.filter(o => o.status === 'ready'), [orders]);
  const pending = useMemo(
    () => [...preparing, ...ready].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [preparing, ready]
  );

  // Tur 276: bekleme özeti — orders değiştikçe yeniden hesapla (polling refresh otomatik)
  const waitSummary = useMemo(() => {
    const prep = orders.filter(o => o.status === 'preparing');
    const rdy = orders.filter(o => o.status === 'ready');
    if (prep.length === 0) {
      return { preparingCount: prep.length, readyCount: rdy.length, oldestMin: 0, avgMin: 0 };
    }
    const mins = prep.map(o => {
      const ts = o.created_at || o.received_at || o.timestamp;
      return Math.floor((Date.now() - new Date(ts)) / 60000);
    });
    const oldestMin = Math.max(...mins);
    const avgMin = Math.round(mins.reduce((a, b) => a + b, 0) / mins.length);
    return { preparingCount: prep.length, readyCount: rdy.length, oldestMin, avgMin };
  }, [orders]);

  return (
    <FeatureGate feature="kitchen_display">
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* ── Top bar ── */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between gap-3 sticky top-0 z-10">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-2xl">👨‍🍳</span>
          <div className="min-w-0">
            <div className="font-black text-lg leading-none">{t('kitchenScreenTitle')}</div>
            <div className="text-xs text-gray-500 mt-0.5 truncate" aria-live="polite" aria-atomic="true">
              {lastUpdate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Stat pills */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl px-3 py-1.5 text-center">
              <div className="text-lg font-black text-amber-400 leading-none">{pending.length}</div>
              <div className="text-[10px] text-gray-400">{t('kitchenWaitingLabel')}</div>
            </div>
            <div className="bg-green-500/20 border border-green-500/30 rounded-xl px-3 py-1.5 text-center">
              <div className="text-lg font-black text-green-400 leading-none">{stats.served}</div>
              <div className="text-[10px] text-gray-400">{t('kitchenServedLabel')}</div>
            </div>
          </div>
          {/* Tur 141: kanban/list view toggle */}
          <div role="tablist" aria-label={t('viewModeList')} className="flex items-center bg-gray-800 border border-gray-700 rounded-xl p-0.5">
            <button
              role="tab"
              aria-selected={viewMode === 'list'}
              onClick={() => setView('list')}
              aria-label={t('viewModeList')}
              title={t('viewModeList')}
              className={`px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${viewMode === 'list' ? 'bg-gray-700 text-white' : 'text-gray-400'}`}
            >📋</button>
            <button
              role="tab"
              aria-selected={viewMode === 'kanban'}
              onClick={() => setView('kanban')}
              aria-label={t('viewModeKanban')}
              title={t('viewModeKanban')}
              className={`px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${viewMode === 'kanban' ? 'bg-gray-700 text-white' : 'text-gray-400'}`}
            >🗂️</button>
          </div>
          <button
            onClick={loadOrders}
            aria-label={t('refresh')}
            className="bg-gray-800 active:bg-gray-700 border border-gray-700 px-3 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-95"
          >
            🔄
          </button>
          {user && (
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 active:bg-blue-800 px-3 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-95"
            >
              ← {t('kitchenPanelBtn')}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 bg-red-900/40 border border-red-700 text-red-300 px-4 py-3 rounded-xl text-sm flex justify-between">
          {error}
          <button onClick={() => setError('')} aria-label={t('close')} className="ml-2">✕</button>
        </div>
      )}

      {/* ── Mobile tab bar — tek kolon, tab yok ── */}

      {/* Tur 276: 4-kart wait-time + bekleyen sipariş özeti */}
      {!loading && (() => {
        const { preparingCount, readyCount, oldestMin, avgMin } = waitSummary;
        const oldestCritical = oldestMin > 20;
        const minShort = t('kitchenMinShort') || 'dk';
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 px-4 pt-3">
            {/* Hazırlanıyor */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 sm:p-4 text-center">
              <div className="text-base sm:text-2xl font-black text-amber-400 leading-none">{preparingCount}</div>
              <div className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-400 mt-1">{t('statusPreparing') || 'Hazırlanıyor'}</div>
            </div>
            {/* Hazır */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 sm:p-4 text-center">
              <div className="text-base sm:text-2xl font-black text-green-400 leading-none">{readyCount}</div>
              <div className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-400 mt-1">{t('statusReady') || 'Hazır'}</div>
            </div>
            {/* En Eski Sipariş */}
            <div className={`${oldestCritical ? 'bg-red-500/15 border-red-500/40' : 'bg-purple-500/10 border-purple-500/30'} border rounded-xl p-3 sm:p-4 text-center`}>
              <div className={`text-base sm:text-2xl font-black leading-none ${oldestCritical ? 'text-red-400' : 'text-purple-400'}`}>
                {preparingCount > 0 ? `${oldestMin}${minShort}` : '—'}
              </div>
              <div className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-400 mt-1">{t('kitchenOldestOrder') || 'En Eski Sipariş'}</div>
            </div>
            {/* Ortalama Bekleme */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4 text-center">
              <div className="text-base sm:text-2xl font-black text-blue-400 leading-none">
                {preparingCount > 0 ? `${avgMin}${minShort}` : '—'}
              </div>
              <div className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-400 mt-1">{t('kitchenAvgWait') || 'Ortalama Bekleme'}</div>
            </div>
          </div>
        );
      })()}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-lg gap-3">
          <span className="animate-spin text-2xl">🔄</span> {t('loading')}
        </div>
      ) : viewMode === 'kanban' ? (
        // Tur 141: Kanban view — preparing | ready iki sütun
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preparing column */}
            <div>
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-gray-950 py-1 z-[5]">
                <span className="w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
                <h2 className="text-sm font-bold text-amber-400 uppercase tracking-wider">⏳ {t('statusPreparing')} ({preparing.length})</h2>
              </div>
              <div className="space-y-3">
                {preparing.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-800 rounded-2xl p-8 text-center text-gray-600 text-sm">—</div>
                ) : preparing.map(order => (
                  <OrderCard key={order.id} order={order} actionLabel={`✅ ${t('kitchenReadyBtn') || 'Hazır'}`} actionColor="blue" onAction={handleMarkReady} prepEstimate={prepEstimate} nowTs={nowTs} />
                ))}
              </div>
            </div>
            {/* Ready column */}
            <div>
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-gray-950 py-1 z-[5]">
                <span className="w-3 h-3 bg-green-400 rounded-full" />
                <h2 className="text-sm font-bold text-green-400 uppercase tracking-wider">🍽️ {t('statusReady')} ({ready.length})</h2>
              </div>
              <div className="space-y-3">
                {ready.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-800 rounded-2xl p-8 text-center text-gray-600 text-sm">—</div>
                ) : ready.map(order => (
                  <OrderCard key={order.id} order={order} actionLabel={`🚀 ${t('kitchenServeBtn')}`} actionColor="green" onAction={handleMarkServed} prepEstimate={prepEstimate} nowTs={nowTs} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
            <h2 className="text-lg font-black text-amber-400">{t('kitchenWaitingOrders')} ({pending.length})</h2>
          </div>
          {pending.length === 0 ? (
            <EmptyState
              icon="🍳"
              title={t('kitNoItemsTitle') || 'Hazırlanacak sipariş yok'}
              description={t('kitNoItemsDesc') || 'Yeni siparişler geldiğinde mutfak ekranında burada görünecek.'}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {pending.map(order => (
                <OrderCard key={order.id} order={order} actionLabel={`🚀 ${t('kitchenServeBtn')}`} actionColor="green" onAction={handleMarkServed} prepEstimate={prepEstimate} nowTs={nowTs} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
    </FeatureGate>
  );
}
